/**
 * Diff Utilities
 * 
 * Calculate differences between objects for efficient sync.
 * Only changed fields are sent to the server.
 */

/**
 * Calculate the diff between two objects
 * @param {Object} oldObj - Previous state
 * @param {Object} newObj - New state
 * @param {string[]} [ignoreFields] - Fields to ignore in diff
 * @returns {Object|null} - Object with only changed fields, or null if no changes
 */
export function calculateDiff(oldObj, newObj, ignoreFields = ['_id', 'revision', 'updated', 'created', '_localOnly', '_localId']) {
  const diff = {};
  let hasChanges = false;

  // Check for changed/added fields
  for (const key of Object.keys(newObj)) {
    if (ignoreFields.includes(key)) continue;
    
    const oldValue = oldObj?.[key];
    const newValue = newObj[key];
    
    // Deep comparison using JSON (handles arrays and nested objects)
    if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
      diff[key] = newValue;
      hasChanges = true;
    }
  }

  // Check for removed fields (set to null in diff)
  for (const key of Object.keys(oldObj || {})) {
    if (ignoreFields.includes(key)) continue;
    
    if (!(key in newObj)) {
      diff[key] = null;
      hasChanges = true;
    }
  }

  return hasChanges ? diff : null;
}

/**
 * Apply a diff to an object
 * @param {Object} obj - Original object
 * @param {Object} diff - Diff to apply
 * @returns {Object} - New object with diff applied
 */
export function applyDiff(obj, diff) {
  const result = { ...obj };
  
  for (const [key, value] of Object.entries(diff)) {
    if (value === null) {
      delete result[key];
    } else {
      result[key] = value;
    }
  }
  
  return result;
}

/**
 * Merge multiple diffs into one
 * Later diffs override earlier ones
 * @param {Object[]} diffs - Array of diffs to merge
 * @returns {Object} - Merged diff
 */
export function mergeDiffs(diffs) {
  const merged = {};
  
  for (const diff of diffs) {
    if (diff) {
      Object.assign(merged, diff);
    }
  }
  
  return merged;
}

const JUROR_CONTENT_FIELDS = [
  'nameAndId',
  'comments',
  'commentsStyles',
  'demographics',
  'seatNumber',
  'alternateSeatNumber',
];

/**
 * When a locally-created juror is assigned a server id, the create response
 * may echo stale data (e.g. notes typed or a seat move after the create was
 * queued). Keep the user's latest local values for editable fields.
 */
export function mergeCreatedJurorWithLocal(local, serverData, serverId, localId) {
  const merged = {
    ...serverData,
    ...local,
    _id: serverId,
    revision: serverData.revision ?? local.revision,
    updated: serverData.updated ?? local.updated,
    caseId: local.caseId ?? serverData.caseId,
    _synced: true,
    _localOnly: false,
    _localId: localId,
  };

  for (const field of JUROR_CONTENT_FIELDS) {
    if (JSON.stringify(local[field]) !== JSON.stringify(serverData[field])) {
      merged[field] = local[field];
    }
  }

  return merged;
}

/**
 * Check if an object has any pending local changes
 * @param {Object} obj - Object to check
 * @returns {boolean}
 */
export function hasLocalChanges(obj) {
  return obj?._localOnly === true || obj?._localId != null;
}

/**
 * Check if an ID is a local (UUID) ID vs server-assigned
 * @param {string} id - ID to check
 * @returns {boolean}
 */
export function isLocalId(id) {
  // UUIDs have dashes, MongoDB ObjectIds don't
  return typeof id === 'string' && id.includes('-');
}

/**
 * Generate a local UUID for new entities
 * @returns {string}
 */
export function generateLocalId() {
  return crypto.randomUUID();
}

/**
 * Get list of fields that changed between two objects
 * @param {Object} oldObj - Previous state
 * @param {Object} newObj - New state
 * @returns {string[]} - Array of changed field names
 */
export function getChangedFields(oldObj, newObj) {
  const diff = calculateDiff(oldObj, newObj);
  return diff ? Object.keys(diff) : [];
}

export default {
  calculateDiff,
  applyDiff,
  mergeDiffs,
  hasLocalChanges,
  isLocalId,
  generateLocalId,
  getChangedFields
};
